<template>
  <v-data-table :headers="headers" :items="locsPersonal" class="transparent" hide-default-footer disable-pagination>
    <template v-slot:[`item.fecha`]="{ item }">
      {{ humanizeDate(item.fecha) }}
    </template>
    <template v-slot:[`item.fechaBaja`]="{ item }">
      {{ humanizeDate(item.fechaBaja) }}
    </template>
    <template v-slot:[`item.opciones`]="{ item }">
      <span class="d-flex align-center justify-center">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              <IconButton icon="mdi-minus" variant="error" @click="showRemoveConfirmationDialog(item)" />
            </span>
          </template>
          <span>Desvincular LOC</span>
        </v-tooltip>
      </span>
    </template>
  </v-data-table>
</template>

<style scoped>
/* Estilos modernos para LocsPersonalTable */
::v-deep .v-data-table {
  background: transparent !important;
  color: #e0e0e0 !important;
  border-radius: 12px;
}

/* Encabezados de tabla */
::v-deep .v-data-table-header {
  position: relative;
}

::v-deep .v-data-table-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--v-secondary-base);
  opacity: 0.15;
  pointer-events: none;
  z-index: 0;
}

::v-deep .v-data-table-header th {
  color: #e0e0e0 !important;
  font-weight: 700 !important;
  font-size: 0.75rem !important;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-bottom: 2px solid var(--v-secondary-base) !important;
  padding: 16px !important;
  height: 48px !important;
  position: relative;
  z-index: 1;
}

/* Filas de la tabla */
::v-deep .v-data-table tbody tr {
  transition: all 0.2s ease;
  position: relative;
  cursor: pointer;
}

::v-deep .v-data-table tbody tr:hover {
  background: rgba(var(--v-secondary-base-rgb), 0.12) !important;
  transform: scale(1.005);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

::v-deep .v-data-table tbody td {
  color: #e0e0e0 !important;
  border-bottom: 1px solid rgba(var(--v-secondary-base-rgb), 0.08) !important;
  padding: 14px 16px !important;
  position: relative;
  z-index: 1;
  font-size: 0.9375rem;
}

/* Empty state */
::v-deep .v-data-table__empty-wrapper {
  color: rgba(224, 224, 224, 0.6) !important;
}

/* Botones en la tabla */
::v-deep .v-btn {
  border-radius: 12px !important;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important;
  position: relative;
  overflow: hidden;
}

::v-deep .v-btn::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.6s, height 0.6s;
  pointer-events: none;
}

::v-deep .v-btn:active::before {
  width: 300px;
  height: 300px;
}

::v-deep .v-btn:hover:not(:disabled) {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.3);
}

::v-deep .v-btn .v-icon {
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

::v-deep .v-btn:hover .v-icon {
  transform: scale(1.15) rotate(5deg);
}

/* Tooltips */
::v-deep .v-tooltip__content {
  background: rgba(30, 30, 46, 0.95) !important;
  backdrop-filter: blur(20px);
  border: 1px solid var(--v-secondary-base) !important;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
  color: #e0e0e0 !important;
  font-weight: 500;
  font-size: 0.875rem;
  padding: 8px 12px;
}

/* Scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: rgba(10, 10, 10, 0.5);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: var(--v-secondary-base);
  border-radius: 4px;
  transition: all 0.3s;
}

::-webkit-scrollbar-thumb:hover {
  filter: brightness(1.2);
}

/* Animación de entrada */
::v-deep .v-data-table {
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>

<script>
import { API_DATE_FORMAT } from '@/const'
import DateAdapter from '@src/utils/DateAdapter'
import { DIALOG_TYPES } from '@src/plugins/dialog-types'
import { mapActions, mapMutations } from 'vuex'
import IconButton from '../../base/IconButton.vue'

export default {
  components: {
    IconButton,
  },
  props: {
    locsPersonal: {
      type: Array,
      required: true,
      default: () => [],
    },
  },

  data() {
    return {
      headers: [
        { text: 'No. de licencia', value: 'Loc.noLicencia', width: '30%' },
        { text: 'Fecha de alta', value: 'fecha', width: '30%' },
        { text: 'Fecha de baja', value: 'fechaBaja', width: '30%' },
        { text: 'Opciones', value: 'opciones', align: 'center', width: '10%', sortable: false },
      ],
    }
  },

  methods: {
    ...mapMutations('dialog', ['SET_DIALOG', 'CLOSE_DIALOG']),
    ...mapActions('armeria', ['removePersonalLoc']),
    ...mapActions('personalLoc', ['fetchPersonalLoc']),

    humanizeDate(date) {
      if (date) {
        return new DateAdapter(date, API_DATE_FORMAT).toHumanReadable()
      }

      return '-'
    },

    showRemoveConfirmationDialog(locPersonal) {
      this.SET_DIALOG({
        ...DIALOG_TYPES.CONFIRMACION_REMOVER_PERSONAL_LOC,
        props: {
          onConfirm: async () => {
            await this.removePersonalLoc({
              idPersonal: locPersonal.fk_idPersonal,
              idPersonalLoc: locPersonal.idPersonalLoc,
            })
            await this.fetchPersonalLoc(locPersonal.fk_idPersonal)
            this.CLOSE_DIALOG()
          },
        },
      })
    },
  },
}
</script>
